// Tuile d'un indicateur des recettes (REC-02 à REC-08) : valeur servie par GET /indicateurs/valeur,
// états écrits (non renseigné, module non activé, service indisponible), lien vers la liste
// explicative calculée par la même requête de base (R16).
import { Lien, Squelette } from '@cc/ui';
import { ErreurApi } from '../../api/erreurs';
import { fraicheur } from '../../commun/format';
import { francs } from './donnees';
import { useValeurRec, type PeriodeIndicateur } from './requetes';
import styles from './recettes.module.css';

export interface DefinitionRec {
  code: string;
  libelle: string;
  description: string;
  unite: 'montant' | 'taux' | 'nombre';
  /** Situation à date : la période ne s'applique pas. */
  instantane?: boolean;
  ton?: 'retard' | 'ok';
}

export const TUILES_REC: DefinitionRec[] = [
  { code: 'REC-02', libelle: 'Créances émises', description: 'Montant dû des créances émises dans la période, hors annulées', unite: 'montant' },
  { code: 'REC-04', libelle: 'Encaissements confirmés', description: 'Paiements confirmés dans la période, caisse vérifiée comprise, nets des remboursements ordonnés', unite: 'montant', ton: 'ok' },
  { code: 'REC-03', libelle: 'Encaissé en caisse, à vérifier', description: 'Reçus de caisse émis ou clôturés, non encore vérifiés par le financier', unite: 'montant', instantane: true },
  { code: 'REC-05', libelle: 'Reste à recouvrer', description: 'Reste dû des créances ni soldées ni annulées', unite: 'montant', instantane: true },
  { code: 'REC-07', libelle: 'Impayés échus', description: 'Reste dû des créances non soldées dont l\'échéance est dépassée', unite: 'montant', instantane: true, ton: 'retard' },
  { code: 'REC-06', libelle: 'Taux de recouvrement', description: 'Part payée des créances émises dans la période', unite: 'taux' },
  { code: 'REC-08', libelle: 'Anomalies ouvertes', description: 'Anomalies ouvertes ou en instruction, par type', unite: 'nombre', instantane: true },
];

export function definitionRec(code: string): DefinitionRec | undefined {
  return TUILES_REC.find((t) => t.code === code);
}

export function hrefListeRec(code: string, periode: PeriodeIndicateur): string {
  return `/recettes/indicateurs/${code}?debut=${periode.debut}&fin=${periode.fin}`;
}

/** Valeur lisible selon l'unité de la fiche : francs, pour cent ou nombre. */
export function valeurLisible(valeur: number, unite: DefinitionRec['unite']): string {
  if (unite === 'montant') return francs(valeur);
  if (unite === 'taux') return `${valeur.toLocaleString('fr-FR')} %`;
  return valeur.toLocaleString('fr-FR');
}

export function TuileRecette({ definition, periode, actif = true }: { definition: DefinitionRec; periode: PeriodeIndicateur; actif?: boolean }) {
  const valeur = useValeurRec(definition.code, periode, actif);
  let contenu;
  if (!actif) contenu = <p className={styles.etatTuile}>Module non activé</p>;
  else if (valeur.isPending) contenu = <Squelette hauteurBloc={36} libelle={`Calcul de ${definition.libelle}`} />;
  else if (valeur.error) {
    const indisponible = valeur.error instanceof ErreurApi && valeur.error.routeIndisponible;
    contenu = (
      <>
        <p className={styles.etatTuile}>{indisponible ? 'Service des indicateurs non disponible' : 'Valeur indisponible'}</p>
        <p className={styles.fraicheur}>{indisponible ? 'Le serveur déployé n\'expose pas cet indicateur.' : valeur.error instanceof Error ? valeur.error.message : ''}</p>
      </>
    );
  } else if (valeur.data.couverture.etat === 'non_active') contenu = <p className={styles.etatTuile}>Module non activé</p>;
  else if (valeur.data.valeur === null || valeur.data.couverture.etat === 'non_renseigne') {
    contenu = (
      <>
        <p className={styles.etatTuile}>Non renseigné</p>
        <p className={styles.fraicheur}>Aucune opération de recettes enregistrée pour cette période.</p>
      </>
    );
  } else {
    contenu = (
      <>
        <p className={styles.valeur} data-ton={definition.ton} data-testid={`valeur-${definition.code}`}>
          {valeurLisible(valeur.data.valeur, definition.unite)}
        </p>
        <p className={styles.fraicheur}>Calculé {fraicheur(valeur.data.calcule_le)}</p>
      </>
    );
  }
  return (
    <article className={styles.tuile} aria-labelledby={`tuile-${definition.code}`}>
      <header>
        <h3 id={`tuile-${definition.code}`}>{definition.libelle}</h3>
        <span className={styles.code}>{definition.code}</span>
      </header>
      {contenu}
      <p className={styles.note}>
        {definition.description}
        {definition.instantane ? ' (situation à date)' : ''}
      </p>
      <Lien href={hrefListeRec(definition.code, periode)} className={styles.ouvrir} aria-label={`Ouvrir la liste : ${definition.libelle}`}>
        Ouvrir la liste
      </Lien>
    </article>
  );
}
